import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box as MuiBox, CircularProgress, Alert, Button as MuiButton } from '@mui/material';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link as RouterLink
} from "react-router-dom";

import {
    Container,
    AppBar,
    Toolbar,
    Box,
    Button,
    Menu,
    MenuItem,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Link
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import rules from "../rbac-rules";

import Home from './Home';
import Stanza from './stanzaWebex/Stanza';
import ReqLab from './reqLab/ReqLab';
import ReqExamLab from './reqExamLab/ReqExamLab';
import LabInventory from './labInventory/LabInventory';
import PubLab from './pubLab/PubLab';
import RegisterLab from './pubLab/RegisterLab';
import PubLabDettPage from './pubLab/PubLabDettPage';
import LabConsolle from './labConsolle/LabConsolle';
import LabConsolleSchedule from './labConsolle/LabConsolleSchedule';
import PredefinedLabsPage from './predefinedLab/PredefinedLabsPage';
import ManagePredefinedAppVolCatalog from './labConsolle/ManagePredefinedAppVolCatalog';
import LabStats from './labStats/LabStats';

// Funzione di utilità per verificare i permessi
const checkPermission = (groups, action) => {
    if (!groups || groups.length === 0) return false;
    return groups.some(role => {
        const permissions = rules[role];
        return permissions && permissions.static && permissions.static.includes(action);
    });
};


function BootstrapNavbar() {
    const [labConsolleKey, setLabConsolleKey] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [guideAnchor, setGuideAnchor] = useState(null);
    const [teacherAnchor, setTeacherAnchor] = useState(null);
    const [adminAnchor, setAdminAnchor] = useState(null);

    // Ottieni dati auth dal context
    const { authInfo, studentInfo, isLoading, isError, error, retry } = useAuth();

    const handleLabConsolleClick = () => {
        // Incrementa la chiave per forzare il remount
        setLabConsolleKey((prevKey) => prevKey + 1);
    };

    const handleMobileMenuToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuClose = () => {
        setGuideAnchor(null);
        setTeacherAnchor(null);
        setAdminAnchor(null);
    };

    // Loading state
    if (isLoading) {
        return (
            <MuiBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', gap: 2 }}>
                <CircularProgress sx={{ color: '#a71e3b' }} />
                <span>Caricamento in corso...</span>
            </MuiBox>
        );
    }

    // Error state (errori di rete/server, NON 401)
    if (isError) {
        return (
            <MuiBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', gap: 2, p: 3 }}>
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    Errore durante l'autenticazione: {error}
                </Alert>
                <MuiButton variant="contained" onClick={retry} sx={{ bgcolor: '#a71e3b', '&:hover': { bgcolor: '#8a1830' } }}>
                    Riprova
                </MuiButton>
            </MuiBox>
        );
    }

    // Estrai dati dal context (valori di default se non autenticato)
    const { apiAuthResponse, apiAuthName, apiAuthSurname, apiAuthGroups } = authInfo || {};
    const { matricola: stuMatricola, indiRes: stuIndiRes, indiDom: stuIndiDom } = studentInfo || {};

    // Verifica permessi per le aree del menu
    const groups = apiAuthGroups;
    const canVisitGuide = checkPermission(groups, "labGuide-page:visit");
    const canVisitTeacherArea = checkPermission(groups, "teacherArea:visible");
    const canVisitAdminArea = checkPermission(groups, "adminLabArea:visible");
    const canVisitLabRequest = checkPermission(groups, "labRequest-page:visit");
    const canVisitLabConsolle = checkPermission(groups, "labConsolle-page:visit");
    const canVisitLabExamRequest = checkPermission(groups, "labExamRequest-page:visit");
    const canVisitLabPredefiniti = checkPermission(groups, "labPredefiniti-page:visit");

    // Determina se mostrare i menu principali (solo se hanno almeno un item visibile)
    const showDocenteMenu = canVisitTeacherArea && (canVisitAdminArea || canVisitLabRequest);
    const showAdminMenu = canVisitAdminArea && (canVisitLabConsolle || canVisitLabExamRequest);
    const showGuideMenu = canVisitGuide;

    return (
        <React.Fragment>
            <Router>
                <AppBar position="static" sx={{ bgcolor: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
                    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        {/* Logo */}
                        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <img
                                src="/logo_libaas.png"
                                alt="Logo LIBaaS"
                                style={{ height: '60px', width: 'auto', display: 'block' }}
                            />
                        </Box>

                        {/* Desktop Menu */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0 }}>
                            <Link component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
                                <Button
                                    sx={{
                                        color: '#333',
                                        fontWeight: 500,
                                        fontSize: '1rem',
                                        textTransform: 'none'
                                    }}
                                >
                                    Home
                                </Button>
                            </Link>

                            {/* Area Docente Dropdown */}
                            {showDocenteMenu && (
                                <>
                                    <Button
                                        onClick={(e) => setTeacherAnchor(e.currentTarget)}
                                        sx={{ color: '#333', fontWeight: 500, fontSize: '1rem', textTransform: 'none' }}
                                    >
                                        Area docente
                                    </Button>
                                    <Menu
                                        anchorEl={teacherAnchor}
                                        open={Boolean(teacherAnchor)}
                                        onClose={handleMenuClose}
                                    >
                                        {canVisitAdminArea && (
                                            <MenuItem component={RouterLink} to="/ReqLabV2" onClick={handleMenuClose}>Richiesta nuovo laboratorio lezioni Horizon Ver.2</MenuItem>
                                        )}
                                        {canVisitLabRequest && (
                                            <MenuItem component={RouterLink} to="/PubLab" onClick={handleMenuClose}>Gestione laboratori esistenti</MenuItem>
                                        )}
                                    </Menu>
                                </>
                            )}

                            {/* Amministrazione Dropdown */}
                            {showAdminMenu && (
                                <>
                                    <Button
                                        onClick={(e) => setAdminAnchor(e.currentTarget)}
                                        sx={{ color: '#333', fontWeight: 500, fontSize: '1rem', textTransform: 'none' }}
                                    >
                                        Amministrazione
                                    </Button>
                                    <Menu
                                        anchorEl={adminAnchor}
                                        open={Boolean(adminAnchor)}
                                        onClose={handleMenuClose}
                                    >
                                        {canVisitLabConsolle && (
                                            <MenuItem component={RouterLink} to="/labConsolle" onClick={() => { handleLabConsolleClick(); handleMenuClose(); }}>Consolle Laboratori</MenuItem>
                                        )}
                                        {canVisitLabConsolle && (
                                            <MenuItem component={RouterLink} to="/labConsolleSchedule" onClick={handleMenuClose}>Vista pianificazione Laboratori</MenuItem>
                                        )}
                                        {canVisitLabExamRequest && (
                                            <MenuItem component={RouterLink} to="/LabInventory" onClick={handleMenuClose}>Inventario LIB (laboratori fisici)</MenuItem>
                                        )}
                                        {canVisitAdminArea && (
                                            <MenuItem component={RouterLink} to="/ReqLabV2" onClick={handleMenuClose}>Richiesta nuovo laboratorio lezioni Horizon Ver.2</MenuItem>
                                        )}
                                        {canVisitAdminArea && (
                                            <MenuItem component={RouterLink} to="/ReqExamLabV2" onClick={handleMenuClose}>Richiesta nuovo laboratorio esame Horizon Ver.2</MenuItem>
                                        )}
                                        {canVisitLabConsolle && (
                                            <MenuItem component={RouterLink} to="/catalogoAppVolPredefiniti" onClick={handleMenuClose}>Catalogo AppVol Predefiniti</MenuItem>
                                        )}
                                        {canVisitLabConsolle && (
                                            <MenuItem component={RouterLink} to="/labStats" onClick={handleMenuClose}>Statistiche Laboratori</MenuItem>
                                        )}
                                    </Menu>
                                </>
                            )}

                            {/* Guide Dropdown */}
                            {showGuideMenu && (
                                <>
                                    <Button
                                        onClick={(e) => setGuideAnchor(e.currentTarget)}
                                        sx={{ color: '#333', fontWeight: 500, fontSize: '1rem', textTransform: 'none' }}
                                    >
                                        Guide
                                    </Button>
                                    <Menu
                                        anchorEl={guideAnchor}
                                        open={Boolean(guideAnchor)}
                                        onClose={handleMenuClose}
                                    >
                                        <MenuItem
                                            component={Link}
                                            href={process.env.REACT_APP_URL_VDI_DOC}
                                            target="_blank"
                                            onClick={handleMenuClose}
                                        >
                                            Documentazione LIBaaS
                                        </MenuItem>
                                        <MenuItem
                                            component={Link}
                                            href={process.env.REACT_APP_URL_SERVICEDESK}
                                            target="_blank"
                                            onClick={handleMenuClose}
                                        >
                                            Apri una richiesta di assistenza
                                        </MenuItem>
                                        <MenuItem component={RouterLink} to="/Stanza" onClick={handleMenuClose}>Stanza di helpdesk (137 820 4977)</MenuItem>
                                    </Menu>
                                </>
                            )}

                            {/* Lab Predefiniti */}
                            {canVisitLabPredefiniti && (
                                <Link component={RouterLink} to="/labPredefiniti" sx={{ textDecoration: 'none' }}>
                                    <Button
                                        sx={{
                                            color: '#333',
                                            fontWeight: 500,
                                            fontSize: '1rem',
                                            textTransform: 'none'
                                        }}
                                    >
                                        Lab Predefiniti
                                    </Button>
                                </Link>
                            )}

                            {/* Login/Logout */}
                            {apiAuthResponse ? (
                                <Button
                                    href={process.env.REACT_APP_URL_AUTH_LOGOUT}
                                    sx={{ color: '#a71e3b', fontWeight: 600, fontSize: '1rem', textTransform: 'none', ml: 2 }}
                                >
                                    Logout
                                </Button>
                            ) : (
                                <Button
                                    href={process.env.REACT_APP_URL_AUTH_LOGIN}
                                    sx={{ color: '#a71e3b', fontWeight: 600, fontSize: '1rem', textTransform: 'none', ml: 2 }}
                                >
                                    Login
                                </Button>
                            )}
                        </Box>

                        {/* Mobile Menu Button */}
                        <IconButton
                            sx={{ display: { xs: 'flex', md: 'none' }, color: '#333' }}
                            onClick={handleMobileMenuToggle}
                        >
                            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Mobile Drawer */}
                <Drawer
                    anchor="top"
                    open={mobileOpen}
                    onClose={handleMobileMenuToggle}
                >
                    <Box sx={{ width: '100%', p: 2 }}>
                        <List>
                            <ListItem button component={RouterLink} to="/" onClick={handleMobileMenuToggle}>
                                <ListItemText primary="Home" />
                            </ListItem>
                            {showDocenteMenu && (
                                <>
                                    <ListItem><ListItemText primary="Area docente" sx={{ fontWeight: 600 }} /></ListItem>
                                    {canVisitAdminArea && <ListItem button component={RouterLink} to="/ReqLabV2" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Richiesta nuovo laboratorio" /></ListItem>}
                                    {canVisitLabRequest && <ListItem button component={RouterLink} to="/PubLab" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Gestione laboratori" /></ListItem>}
                                </>
                            )}
                            {showAdminMenu && (
                                <>
                                    <ListItem><ListItemText primary="Amministrazione" sx={{ fontWeight: 600 }} /></ListItem>
                                    {canVisitLabConsolle && <ListItem button component={RouterLink} to="/labConsolle" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Consolle Laboratori" /></ListItem>}
                                    {canVisitLabConsolle && <ListItem button component={RouterLink} to="/labConsolleSchedule" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Vista pianificazione" /></ListItem>}
                                    {canVisitLabExamRequest && <ListItem button component={RouterLink} to="/LabInventory" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Inventario LIB" /></ListItem>}
                                    {canVisitAdminArea && <ListItem button component={RouterLink} to="/ReqLabV2" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Lab lezioni Ver.2" /></ListItem>}
                                    {canVisitAdminArea && <ListItem button component={RouterLink} to="/ReqExamLabV2" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Lab esame Ver.2" /></ListItem>}
                                    {canVisitLabConsolle && <ListItem button component={RouterLink} to="/catalogoAppVolPredefiniti" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Catalogo AppVol Predefiniti" /></ListItem>}
                                </>
                            )}
                            {canVisitLabPredefiniti && (
                                <ListItem button component={RouterLink} to="/labPredefiniti" onClick={handleMobileMenuToggle}>
                                    <ListItemText primary="Lab Predefiniti" />
                                </ListItem>
                            )}
                            {showGuideMenu && (
                                <>
                                    <ListItem><ListItemText primary="Guide" sx={{ fontWeight: 600 }} /></ListItem>
                                    <ListItem button component={Link} href={process.env.REACT_APP_URL_VDI_DOC} target="_blank" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Documentazione LIBaaS" /></ListItem>
                                    <ListItem button component={Link} href={process.env.REACT_APP_URL_SERVICEDESK} target="_blank" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Assistenza" /></ListItem>
                                    <ListItem button component={RouterLink} to="/Stanza" onClick={handleMobileMenuToggle} sx={{ pl: 4 }}>
                                        <ListItemText primary="Stanza helpdesk" /></ListItem>
                                </>
                            )}
                        </List>
                    </Box>
                </Drawer>

                <Container>
                    <Routes>
                        <Route path="/" element={
                            <Home apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                                stuMatricola={stuMatricola}
                                stuIndiRes={stuIndiRes}
                                stuIndiDom={stuIndiDom}
                            />}
                        />
                        <Route path="/Stanza" element={
                            <Stanza
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/ReqLab" element={
                            <ReqLab
                                key="ReqLabV1"
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                                labVersion={1}
                            />}
                        />
                        <Route path="/ReqLabV2" element={
                            <ReqLab
                                key="ReqLabV2"
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                                labVersion={2}
                            />}
                        />
                        <Route path="/ReqExamLabV2" element={
                            <ReqExamLab
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                                labVersion={2}
                            />}
                        />
                        <Route path="/LabInventory" element={
                            <LabInventory
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/PubLab" element={
                            <PubLab
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/PubLab/register/:handle" element={
                            <RegisterLab
                                title="Pagina di registrazione ai Laboratori Virtuali"
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />
                        } />
                        <Route path="/labConsolle" element={
                            <LabConsolle
                                key={labConsolleKey}
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/labConsolleSchedule" element={
                            <LabConsolleSchedule
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/catalogoAppVolPredefiniti" element={
                            <ManagePredefinedAppVolCatalog
                                apiAuthGroups={apiAuthGroups}
                                standalone={true}
                            />}
                        />
                        <Route path="/labPredefiniti" element={
                            <PredefinedLabsPage
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/labStats" element={
                            <LabStats
                                apiAuthResponse={apiAuthResponse}
                                apiAuthName={apiAuthName}
                                apiAuthSurname={apiAuthSurname}
                                apiAuthGroups={apiAuthGroups}
                            />}
                        />
                        <Route path="/PubLabDettPage/" exact component={PubLabDettPage} />
                        <Route render={function () {
                            return <p>Pagina non trovata</p>
                        }} />
                    </Routes>

                </Container>
            </Router>
        </React.Fragment >


    );
}




export default BootstrapNavbar;